import { ContextMenuOption } from '@shared/menus/context-menu/context-menu-option/context-menu-option';
import { Icons } from '@core/enums/icons';
import {
  InputDialogComponent,
  InputDialogData,
} from '@shared/components/dialog/input-dialog/input-dialog.component';
import { DialogService } from '@shared/components/dialog/dialog.service';
import { Validators } from '@angular/forms';
import { NotificationService } from '@core/services/notification/notification.service';
import { MatchingSession } from '@data/session/session.model';
import { Stop } from '@data/stop/stop.model';
import { StopActions } from '@data/stop/stop.actions';
import { StopService } from '@data/stop/stop.service';
import { StopDialogHandler } from '../../../../lib/company-elements/services/stop-dialog-handler';

/**
 * Organizer context-menu actions for a {@link Stop}, replacing {@link AttendantMenuOptions}.
 * All Stops are organizer-managed (no external/internal split), so every stop gets the full
 * set: edit (Stop dialog), share edit-link, mail edit-link, delete.
 */
export namespace StopMenuOptions {
  export class DeleteStopOption extends ContextMenuOption<Stop | Stop[]> {
    constructor(
      target: Stop | Stop[],
      private afterClose?: () => void,
    ) {
      super(
        Icons.close,
        'SESSIONS.RUNNING_SESSION.TABLE.COMPANIES_TABLE.ACTIONS.delete_booth',
        target,
      );
    }

    onClick(): void {
      const stops = Array.isArray(this.target) ? this.target : [this.target];
      StopActions.removeManyStops(stops.filter((s): s is Stop => !!s));
      this.afterClose?.apply(this);
    }
  }

  export class EditStopOption extends ContextMenuOption<Stop> {
    constructor(
      target: Stop,
      private session: MatchingSession,
      private stopDialogHandler: StopDialogHandler,
    ) {
      super(Icons.edit, 'SESSIONS.RUNNING_SESSION.TABLE.COMPANIES_TABLE.ACTIONS.edit_booth', target);
    }

    onClick(): void {
      if (!this.target) return;
      this.stopDialogHandler.openEditStopDialog(this.session, this.target, (input) => {
        if (input) StopActions.updateStop(this.target!.id, input);
      });
    }
  }

  export class ShareStopOption extends ContextMenuOption<Stop> {
    constructor(
      target: Stop,
      private session: MatchingSession,
      private notifier: NotificationService,
    ) {
      super('share', 'Bearbeitungslink kopieren', target);
    }

    onClick(): void {
      if (!this.target) return;
      const pin = this.session.pin ? `&pin=${this.session.pin}` : '';
      const url = `https://${window.location.host}/public/stop?s=${this.target.session}&a=${this.target.id}${pin}`;
      navigator.clipboard.writeText(url).then(() => this.notifier.success('Bearbeitungslink kopiert'));
    }
  }

  export class ShareStopAsMailOption extends ContextMenuOption<Stop> {
    constructor(
      target: Stop,
      private notifier: NotificationService,
      private dialog: DialogService<any, any>,
      private stopService: StopService,
    ) {
      super('mail', 'Bearbeitungslink als E-Mail verschicken', target);
    }

    onClick(): void {
      if (!this.target) return;
      this.dialog.openWithCallback({
        type: InputDialogComponent,
        dialogData: {
          title: 'E-Mail für Bearbeitungslink',
          label: 'E-Mail eingeben',
          message:
            'Geben Sie die E-Mail-Adresse ein, an die der Bearbeitungslink gesendet werden soll.',
          validator: Validators.email,
          formErrorMessage: 'Ungültige E-Mail-Adresse',
        } as InputDialogData,
        confirmCallback: (email) => {
          if (!email) return;
          this.stopService
            .sendEditLinkInternal(this.target!.id, email)
            .subscribe(() => this.notifier.success('Bearbeitungslink per E-Mail verschickt'));
        },
      });
    }
  }

  export function getStopMenuOptions(
    stops: Stop | Stop[],
    session: MatchingSession,
    dialog: DialogService<any, any>,
    notifier: NotificationService,
    stopService: StopService,
    stopDialogHandler: StopDialogHandler,
    afterClose?: () => void,
  ): ContextMenuOption<Stop | Stop[]>[] {
    const r: ContextMenuOption<Stop | Stop[]>[] = [];

    let single: Stop | undefined;
    if (Array.isArray(stops)) {
      if (stops.length === 1) single = stops[0];
    } else {
      single = stops;
    }

    if (single) {
      r.push(new EditStopOption(single, session, stopDialogHandler));
      r.push(new ShareStopOption(single, session, notifier));
      r.push(new ShareStopAsMailOption(single, notifier, dialog, stopService));
      r.push(new DeleteStopOption(single, afterClose));
    } else {
      r.push(new DeleteStopOption(stops, afterClose));
    }

    return r;
  }
}
